/**
 * When a bulk send goes out: the "Send at" moment of an absolute-time event,
 * the time zones it can be read in, and the niceness window (no sends at
 * night).
 *
 * The picked date and time are read as a wall clock IN THE CHOSEN ZONE, so
 * "10:00, America/New_York" means 10:00 in New York wherever the operator's
 * browser is. The old app read them in the browser's own zone and only passed
 * the chosen zone along as a label (decided for V2, BHAN-2774).
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/** The business zone: immediate sends and every time shown "in India time". */
export const INDIA_ZONE = 'Asia/Kolkata'

/**
 * Old IANA names some browsers still list (Chrome lists `Asia/Calcutta`, not
 * `Asia/Kolkata`), mapped to the current name, so a zone appears once and
 * under the name people search for. The backend's pytz knows both.
 */
const ZONE_ALIASES = {
    'Asia/Calcutta': 'Asia/Kolkata',
    'Asia/Katmandu': 'Asia/Kathmandu',
    'Asia/Rangoon': 'Asia/Yangon',
    'Asia/Saigon': 'Asia/Ho_Chi_Minh',
    'Europe/Kiev': 'Europe/Kyiv',
    'Atlantic/Faeroe': 'Atlantic/Faroe',
    'America/Godthab': 'America/Nuuk',
    'Pacific/Enderbury': 'Pacific/Kanton',
}

/**
 * Zones offered when the browser cannot list its own
 * (`Intl.supportedValuesOf` is missing before Chrome 99 and Safari 15.4):
 * the countries Bhanzu sends to, plus UTC.
 */
export const FALLBACK_ZONES = [
    'Africa/Johannesburg',
    'Africa/Lagos',
    'Africa/Nairobi',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/New_York',
    'America/Phoenix',
    'America/Toronto',
    'America/Vancouver',
    'Asia/Bahrain',
    'Asia/Dhaka',
    'Asia/Dubai',
    'Asia/Hong_Kong',
    'Asia/Karachi',
    'Asia/Kathmandu',
    'Asia/Kolkata',
    'Asia/Kuala_Lumpur',
    'Asia/Kuwait',
    'Asia/Muscat',
    'Asia/Qatar',
    'Asia/Riyadh',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Adelaide',
    'Australia/Brisbane',
    'Australia/Melbourne',
    'Australia/Perth',
    'Australia/Sydney',
    'Europe/Berlin',
    'Europe/Dublin',
    'Europe/London',
    'Europe/Paris',
    'Pacific/Auckland',
    'UTC',
]

/** The browser's own zone list, or null when it has none. */
const browserZones = () => {
    try {
        if (typeof Intl.supportedValuesOf !== 'function') return null
        const zones = Intl.supportedValuesOf('timeZone')
        return zones?.length ? zones : null
    } catch {
        return null
    }
}

/**
 * The time zones the "Send at" picker offers, A to Z, each once.
 *
 * Old alias names are replaced by their current name and India is always
 * there, since it is the default.
 *
 * @param {string[]|null} [supported] the zone list to start from; the
 *     browser's (`Intl.supportedValuesOf('timeZone')`) by default, else
 *     FALLBACK_ZONES
 * @returns {string[]}
 */
export const timeZoneOptions = (supported = browserZones()) => {
    const source = supported?.length ? supported : FALLBACK_ZONES
    const names = source.map((zone) => ZONE_ALIASES[zone] ?? zone)
    return [...new Set([INDIA_ZONE, ...names])].sort((a, b) =>
        a.localeCompare(b)
    )
}

/** True when `zone` is a time zone this browser can compute. */
export const isValidZone = (zone) => {
    if (!zone || typeof zone !== 'string') return false
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone }).format(0)
        return true
    } catch {
        return false
    }
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^\d{2}:\d{2}(:\d{2})?$/

/**
 * The moment a wall-clock date and time name in a zone, as epoch seconds.
 *
 * `('2026-10-16', '10:00', 'America/New_York')` is 14:00 UTC that day
 * (New York is on summer time), whatever zone the browser is in. Daylight
 * saving is applied for that date, not for today.
 *
 * @param {string} date  'YYYY-MM-DD' (a date input's value)
 * @param {string} time  'HH:mm' (a time input's value)
 * @param {string} zone  an IANA zone name
 * @returns {number|null} null when a part is missing or not valid (a
 *     calendar date that does not exist, such as 2026-02-30, included)
 */
export const wallTimeToEpoch = (date, time, zone) => {
    if (!DATE_RE.test(date ?? '') || !TIME_RE.test(time ?? '')) return null
    if (!isValidZone(zone)) return null
    const moment = dayjs.tz(`${date}T${time}`, zone)
    if (!moment.isValid()) return null
    // dayjs rolls 2026-02-30 over to 2 March; refuse it instead.
    if (moment.tz(zone).format('YYYY-MM-DD') !== date) return null
    return moment.unix()
}

/**
 * Check the "Send at" fields of an absolute-time event.
 *
 * @param {{date: string, time: string, zone: string, nowSeconds: number}} fields
 * @returns {{epoch: number|null, error: string, missing: boolean}} `error` is
 *     '' when the moment can be sent; `missing` marks an error that only asks
 *     for an empty field (shown as a hint, not as a fault)
 */
export const scheduleCheck = ({ date, time, zone, nowSeconds }) => {
    if (!date || !time)
        return {
            epoch: null,
            error: 'Pick the date and time to send at.',
            missing: true,
        }
    if (!zone)
        return {
            epoch: null,
            error: 'Pick the time zone of that date and time.',
            missing: true,
        }
    const epoch = wallTimeToEpoch(date, time, zone)
    if (epoch === null)
        return {
            epoch: null,
            error: 'That date, time or time zone is not valid.',
            missing: false,
        }
    if (epoch <= nowSeconds)
        return {
            epoch,
            error: 'That time has already passed. Pick a later time.',
            missing: false,
        }
    return { epoch, error: '', missing: false }
}

/** `epoch` (seconds) as a dayjs moment in `zone`. */
export const inZone = (epoch, zone) => dayjs.unix(epoch).tz(zone)

/** `epoch` as "Fri 16 Oct 2026, 10:00 AM" on the wall clock of `zone`. */
export const formatInZone = (epoch, zone) =>
    inZone(epoch, zone).format('ddd D MMM YYYY, h:mm A')

/** `epoch` as "Fri 16 Oct 2026, 7:30 PM India time". */
export const formatIndiaTime = (epoch) =>
    `${formatInZone(epoch, INDIA_ZONE)} India time`

/**
 * The niceness window: a niceness event may only go out from 08:00 up to,
 * but not including, 20:00. The backend uses the same bounds when it moves a
 * scheduled comm into the receiver's daytime (`receiver_hour < 8`,
 * `receiver_hour >= 20`, lambda-comms-event-listener `comms_trigger.py`).
 */
export const NICE_START_HOUR = 8
export const NICE_END_HOUR = 20

/** True when `epoch` falls inside the niceness window on `zone`'s clock. */
export const isWithinNiceHours = (epoch, zone) => {
    const hour = inZone(epoch, zone).hour()
    return hour >= NICE_START_HOUR && hour < NICE_END_HOUR
}

/**
 * Why a niceness event cannot go out at `epoch`, or '' when it can.
 *
 * For an immediate send this screen check is the only one: the backend
 * applies niceness to scheduled comms only.
 *
 * @param {{niceness: boolean, epoch: number, zone: string, scheduled: boolean}} send
 *     `zone` is the chosen zone for a scheduled send, India for an immediate one
 * @returns {string}
 */
export const nicenessError = ({ niceness, epoch, zone, scheduled }) => {
    if (!niceness || epoch === null || epoch === undefined) return ''
    if (isWithinNiceHours(epoch, zone)) return ''
    const clock = inZone(epoch, zone).format('h:mm A')
    const where = zone === INDIA_ZONE ? 'India time' : `in ${zone}`
    if (scheduled)
        return `This event only sends between 8 AM and 8 PM, and ${clock} ${where} is outside that. Pick a time between 8 AM and 8 PM.`
    return `This event only sends between 8 AM and 8 PM India time, and it is ${clock} there now. Send it between 8 AM and 8 PM.`
}

/**
 * When a send would go out, and why it cannot, in one rule for the page and
 * for the recheck at Send.
 *
 * An immediate send goes out now and is checked against India time; an
 * absolute-time event goes out at its "Send at" moment, which must be
 * complete, valid and ahead, and is checked in its chosen zone.
 *
 * @param {object} send
 * @param {boolean} send.absoluteTime the event sends at a chosen time
 * @param {boolean} send.niceness the event only sends from 08:00 to 20:00
 * @param {{date: string, time: string, zone: string}} send.schedule
 * @param {number} send.nowSeconds
 * @returns {{scheduled: boolean, epoch: number|null, zone: string,
 *     error: string, missing: boolean}} `epoch` is the moment it goes out
 *     (now for an immediate send); `error` is '' when it can be sent
 */
export const sendTiming = ({
    absoluteTime,
    niceness,
    schedule,
    nowSeconds,
}) => {
    if (!absoluteTime)
        return {
            scheduled: false,
            epoch: nowSeconds,
            zone: INDIA_ZONE,
            error: nicenessError({
                niceness,
                epoch: nowSeconds,
                zone: INDIA_ZONE,
                scheduled: false,
            }),
            missing: false,
        }
    const check = scheduleCheck({ ...schedule, nowSeconds })
    const zone = schedule?.zone ?? ''
    if (check.error) return { scheduled: true, zone, ...check }
    return {
        scheduled: true,
        epoch: check.epoch,
        zone,
        error: nicenessError({
            niceness,
            epoch: check.epoch,
            zone,
            scheduled: true,
        }),
        missing: false,
    }
}
